import { Injectable, inject } from '@angular/core'
import { ChartConfig, GenericChartItem, MetricResult, MetricValue } from 'src/app/types/chart.model'
import { ProcessedJobTask } from 'src/app/types/datasources/tasks.model'
import { DateUtilsService } from '../date-utils.service'
import { UtilsService } from '../utils.service'

import dayjs from 'dayjs'
import dayjsBusinessDays from 'dayjs-business-days2'
import { LogService } from '../log.service'
import { ChartResult, SERIES_EMPTY_TEXT } from './types'
import { TaskDurationAggregation } from 'src/app/types/enums'
import { DatasourceConfigService } from './datasource-config.service'
import { ChartDatasourceHelper } from './chart-datasource-helper.service'
dayjs.extend(dayjsBusinessDays)

export interface DurationResult {
  task2JobNumsWithMatchingTask1: string[]
  sumOfDurationDays: number
  sumOfDurationWeeks: number
  sumOfDurationBusinessDays: number

  // For multiple series support
  seriesResults?: Map<string, SeriesDurationResult>
}

export interface SeriesDurationResult {
  jobNumbers: string[]
  sumOfDurationDays: number
  sumOfDurationWeeks: number
  sumOfDurationBusinessDays: number
}

export interface TaskDurationItem extends ProcessedJobTask {
  durationDays: number
  durationWeeks: number
  durationBusinessDays: number
}

export interface TaskDurationBuildResult {
  items: TaskDurationItem[]
  incompleteTask2JobIds: number[]
  unmatchedTask1JobIds: number[]
}

export function buildTaskDurationResult(
  tasks1: ProcessedJobTask[],
  tasks2: ProcessedJobTask[],
  task1DateField: string,
  task2DateField: string
): TaskDurationBuildResult {
  const jobsWithIncompleteTask2 = new Set(
    tasks2
      .filter((task) => !task[task2DateField as keyof ProcessedJobTask])
      .map((task) => task.jobId)
  )
  const latestTask2ByJob = new Map<number, ProcessedJobTask>()
  const unmatchedTask1JobIds: number[] = []

  for (const task of tasks2) {
    if (jobsWithIncompleteTask2.has(task.jobId)) continue

    const existing = latestTask2ByJob.get(task.jobId)
    const taskDate = task[task2DateField as keyof ProcessedJobTask]
    const existingDate = existing?.[task2DateField as keyof ProcessedJobTask]
    if (!existing || (existingDate && taskDate && existingDate < taskDate)) {
      latestTask2ByJob.set(task.jobId, task)
    }
  }

  const items = Array.from(latestTask2ByJob.values()).flatMap((task2) => {
    const task1Dates = tasks1
      .filter((task) => task.jobId === task2.jobId)
      .map((task) => task[task1DateField as keyof ProcessedJobTask])
      .filter((date): date is Date => date instanceof Date)
      .sort((left, right) => left.getTime() - right.getTime())
    const task1Date = task1Dates[0]
    const task2Date = task2[task2DateField as keyof ProcessedJobTask]
    if (!task1Date || !(task2Date instanceof Date)) {
      unmatchedTask1JobIds.push(task2.jobId)
      return []
    }

    const durationDays = dayjs(task2Date).diff(dayjs(task1Date), 'days')
    return [{
      ...task2,
      durationDays,
      durationWeeks: durationDays / 7,
      durationBusinessDays: dayjs(task2Date).businessDiff(dayjs(task1Date)),
    }]
  })

  return {
    items,
    incompleteTask2JobIds: Array.from(jobsWithIncompleteTask2).sort((left, right) => left - right),
    unmatchedTask1JobIds: unmatchedTask1JobIds.sort((left, right) => left - right),
  }
}

export function buildTaskDurationItems(
  tasks1: ProcessedJobTask[],
  tasks2: ProcessedJobTask[],
  task1DateField: string,
  task2DateField: string
): TaskDurationItem[] {
  return buildTaskDurationResult(tasks1, tasks2, task1DateField, task2DateField).items
}

@Injectable({
  providedIn: 'root',
})
export class TaskDurationService {
  private dateUtils = inject(DateUtilsService);
  private logger = inject(LogService);
  private utils = inject(UtilsService);
  private datasourceConfig = inject(DatasourceConfigService);


  processTaskDurations(
    tasks1: ProcessedJobTask[],
    tasks2: ProcessedJobTask[],
    task1Name: string,
    task2Name: string,
    config: ChartConfig
  ): ChartResult {
    if (!config.task1DateField || !config.task2DateField) {
      this.logger.log('task-duration', 'processTaskDurations', null, 'no task date fields')
      throw Error('no task date fields when fetching task duration source')
    }

    console.groupCollapsed('Task Duration debugging - ' + config.cardTitle)

    console.debug('Task duration processing for >> ' + config.cardTitle)
    console.debug('1st tasks:')
    console.debug(tasks1)
    console.debug('2nd tasks:')
    console.debug(tasks2)

    const durationResult = buildTaskDurationResult(
      tasks1,
      tasks2,
      config.task1DateField,
      config.task2DateField
    )
    const durationItems = durationResult.items

    if (durationResult.incompleteTask2JobIds.length) {
      console.debug(
        `Excluded jobs with any task 2 missing ${config.task2DateField}:`,
        durationResult.incompleteTask2JobIds
      )
    }
    if (durationResult.unmatchedTask1JobIds.length) {
      console.debug(
        `Excluded task 2 jobs with no matching task 1 ${config.task1DateField}:`,
        durationResult.unmatchedTask1JobIds
      )
    }

    const groupKeyFunc = this.dateUtils.getSimpleGroupKeyFunc(
      config.argumentFieldIsDate,
      config.dateAxisTypeId
    )
    const groupedTask2 = this.utils.groupBy(
      durationItems,
      config.argumentField,
      groupKeyFunc,
      config.argumentFieldIsDate ? undefined : SERIES_EMPTY_TEXT
    ) as unknown as Record<string, ProcessedJobTask[]>
    console.debug('grouped tasks:')
    console.debug(groupedTask2)

    // Check if we're splitting by a field to create multiple series
    const formatDateFunc = this.dateUtils.formatDatePadded

    // Create FilteredItems structure to reuse createChartSeries method
    const filteredItems = {
      filteredIds: [],
      items: durationItems,
    }

    const [splittingByDateField, isMultipleSeries, chartSeriesObjects] =
      this.utils.createChartSeries(filteredItems, config, formatDateFunc)

    // Extract series names from ChartSeries objects
    const chartSeries = chartSeriesObjects.map((s) => s.value)

    // foreach grouping, foreach job in task2 list
    // calc duration in days for task1 min date -> task 2 max date
    // and get average over jobs foreach date period
    const chartItems: GenericChartItem[] = []
    const groupings = Object.keys(groupedTask2)
    groupings.forEach((grouping) => {
      console.debug('GROUPING: ' + grouping)
      const task2sForCurrentGrp = groupedTask2[grouping] as TaskDurationItem[]
      const result = {
        task2JobNumsWithMatchingTask1: [],
        sumOfDurationDays: 0,
        sumOfDurationWeeks: 0,
        sumOfDurationBusinessDays: 0,
        seriesResults: isMultipleSeries ? new Map<string, SeriesDurationResult>() : undefined,
      } as DurationResult

      // Initialize series results if splitting
      if (isMultipleSeries && result.seriesResults) {
        chartSeries.forEach((seriesValue) => {
          result.seriesResults!.set(seriesValue, {
            jobNumbers: [],
            sumOfDurationDays: 0,
            sumOfDurationWeeks: 0,
            sumOfDurationBusinessDays: 0,
          })
        })
      }

      task2sForCurrentGrp.forEach((item) => {
        this.addDurationToResult(
          item,
          result,
          isMultipleSeries ? config.splitByField : undefined,
          splittingByDateField,
          formatDateFunc
        )
      })

      const item = this.assembleChartItem(
        result,
        config,
        task1Name,
        task2Name,
        grouping,
        task2sForCurrentGrp.length,
        isMultipleSeries,
        chartSeries
      )

      chartItems.push(item)
    })

    const filteredChartSeries = this.filterSeriesWithNoData(
      chartItems,
      chartSeries,
      isMultipleSeries
    )

    const hasDynamicTarget = ChartDatasourceHelper.buildTargetData(chartItems, config)

    console.groupEnd()

    return {
      data: chartItems,
      splitSeries: isMultipleSeries,
      dynamicSeries: isMultipleSeries
        ? filteredChartSeries.map((s) => ({ label: s, value: s }))
        : undefined,
      hasDynamicTarget,
    }
  }

  processTaskDurationMetric(
    tasks1: ProcessedJobTask[],
    tasks2: ProcessedJobTask[],
    config: ChartConfig
  ): MetricResult {
    if (!config.task1DateField || !config.task2DateField) {
      this.logger.log('task-duration', 'processTaskDurationMetric', null, 'no task date fields')
      throw Error('no task date fields when fetching task duration source')
    }

    const items = buildTaskDurationItems(
      tasks1,
      tasks2,
      config.task1DateField,
      config.task2DateField
    )
    const splitValues = this.utils.isMultipleSeries(config)
      ? this.buildMetricSplitValues(items, config)
      : undefined

    return {
      value: this.getAverageDuration(items, config.taskDurationAggregation),
      numItems: items.length,
      jobIds: this.getJobNumbers(items),
      splitValues,
    }
  }

  private buildMetricSplitValues(
    items: TaskDurationItem[],
    config: ChartConfig
  ): MetricValue[] {
    const groups = new Map<string, TaskDurationItem[]>()
    for (const item of items) {
      const rawLabel = item[config.splitByField as keyof TaskDurationItem]
      const label = rawLabel == null || rawLabel === '' ? SERIES_EMPTY_TEXT : String(rawLabel)
      const group = groups.get(label) ?? []
      group.push(item)
      groups.set(label, group)
    }

    return Array.from(groups, ([label, group]) => ({
      label,
      value: this.getAverageDuration(group, config.taskDurationAggregation),
      numItems: group.length,
      jobIds: this.getJobNumbers(group),
    }))
  }

  private getAverageDuration(
    items: TaskDurationItem[],
    aggregation: TaskDurationAggregation
  ): number {
    return this.getAverageDurationFromTotals(
      items.reduce((sum, item) => sum + item.durationDays, 0),
      items.reduce((sum, item) => sum + item.durationBusinessDays, 0),
      items.reduce((sum, item) => sum + item.durationWeeks, 0),
      items.length,
      aggregation
    )
  }

  private getAverageDurationFromTotals(
    durationDays: number,
    durationBusinessDays: number,
    durationWeeks: number,
    numJobs: number,
    aggregation: TaskDurationAggregation
  ): number {
    if (!numJobs) return 0

    switch (aggregation) {
      case TaskDurationAggregation.Day:
        return Math.ceil(durationDays / numJobs)
      case TaskDurationAggregation.BusinessDay:
        return Math.ceil(durationBusinessDays / numJobs)
      case TaskDurationAggregation.Week:
      default:
        return this.utils.round(durationWeeks / numJobs, 1)
    }
  }

  private getJobNumbers(items: TaskDurationItem[]): string[] {
    return Array.from(new Set(items.map((item) => item.jobNumber))).sort()
  }

  private assembleChartItem(
    result: DurationResult,
    config: ChartConfig,
    task1Name: string,
    task2Name: string,
    grouping: string,
    numItems: number,
    isMultipleSeries: boolean = false,
    chartSeries: string[] = []
  ) {
    const numJobsAveraged = result.task2JobNumsWithMatchingTask1.length

    let averageDurationDays = 0
    let averageDurationBusinessDays = 0
    let averageDurationWeeks = 0
    if (numJobsAveraged) {
      averageDurationDays = Math.ceil(result.sumOfDurationDays / numJobsAveraged)
      averageDurationBusinessDays = Math.ceil(result.sumOfDurationBusinessDays / numJobsAveraged)
      averageDurationWeeks = this.utils.round(result.sumOfDurationWeeks / numJobsAveraged, 1)
    }

    const value = this.getAverageDurationFromTotals(
      result.sumOfDurationDays,
      result.sumOfDurationBusinessDays,
      result.sumOfDurationWeeks,
      numJobsAveraged,
      config.taskDurationAggregation
    )
    let valueAxisString = ''
    switch (config.taskDurationAggregation) {
      case TaskDurationAggregation.Day:
        valueAxisString = 'Av. Days'
        break
      case TaskDurationAggregation.BusinessDay:
        valueAxisString = 'Av. Business Days'
        break
      case TaskDurationAggregation.Week:
        valueAxisString = 'Av. Weeks'
        break
      default:
        config.taskDurationAggregation = TaskDurationAggregation.Week
    }

    config.hideDefaultValueTooltipLines = true
    config.valueAxisTitle = valueAxisString
    config.tooltipTitle =
      `Average duration between <i>${config.task1DateField}</i>` +
      ` of <b>${task1Name}</b> and <i>${config.task2DateField}</i> of <b>${task2Name}</b>`

    const item: GenericChartItem = {
      groupField: config.argumentFieldIsDate ? new Date(grouping) : grouping,
      numItems: numItems,
      value: value,
      seriesNumItems: {},
      additionalPointInfoValues: [
        { label: 'Average duration WEEKS', value: averageDurationWeeks.toString() },
        { label: 'Average duration DAYS', value: averageDurationDays.toString() },
        {
          label: 'Average duration WORKING DAYS',
          value: averageDurationBusinessDays.toString(),
        },
        {
          label: 'Num jobs averaged for this period',
          value: numJobsAveraged.toString(),
        },
      ],
      jobIds: result.task2JobNumsWithMatchingTask1,
    }

    // Populate dynamic series values if splitting
    if (isMultipleSeries && result.seriesResults) {
      chartSeries.forEach((seriesName) => {
        const seriesResult = result.seriesResults!.get(seriesName)
        if (seriesResult) {
          const numJobsInSeries = seriesResult.jobNumbers.length
          const seriesValue = this.getAverageDurationFromTotals(
            seriesResult.sumOfDurationDays,
            seriesResult.sumOfDurationBusinessDays,
            seriesResult.sumOfDurationWeeks,
            numJobsInSeries,
            config.taskDurationAggregation
          )

          item[seriesName] = seriesValue
          item.seriesNumItems[seriesName] = numJobsInSeries
        }
      })
    }

    return item
  }

  /** updates 'result' param */
  private addDurationToResult(
    item: TaskDurationItem,
    result: DurationResult,
    splitByField?: string,
    splittingByDateField?: boolean,
    formatDateFunc?: (d: Date | string | undefined) => string
  ) {
    result.sumOfDurationDays += item.durationDays
    result.sumOfDurationBusinessDays += item.durationBusinessDays
    result.sumOfDurationWeeks = result.sumOfDurationDays / 7
    result.task2JobNumsWithMatchingTask1.push(item.jobNumber)

    // If splitting by series, use the selected latest task 2 record for the job.
    if (splitByField && result.seriesResults) {
      const rawSeriesValue = item[splitByField as keyof TaskDurationItem]

      // Format the series value to match the series names created earlier
      let seriesValue: string | number
      if (rawSeriesValue === null || rawSeriesValue === undefined) {
        seriesValue = this.utils.checkForNullOrEmpty('')
      } else if (typeof rawSeriesValue === 'boolean') {
        seriesValue = String(rawSeriesValue)
      } else {
        seriesValue = this.utils.checkForNullOrEmpty(rawSeriesValue as string | number)
      }

      // Format date if needed
      const seriesKey =
        splittingByDateField && formatDateFunc
          ? formatDateFunc(seriesValue as Date | string | undefined)
          : String(seriesValue)

      const seriesResult = result.seriesResults.get(seriesKey)
      if (seriesResult) {
        seriesResult.sumOfDurationDays += item.durationDays
        seriesResult.sumOfDurationBusinessDays += item.durationBusinessDays
        seriesResult.sumOfDurationWeeks = seriesResult.sumOfDurationDays / 7
        seriesResult.jobNumbers.push(item.jobNumber)
      }
    }
  }

  /**
   * Filters out series that have no data (all zeros or no jobs) across all periods.
   * Also removes the series properties from chart items that don't have data.
   */
  private filterSeriesWithNoData(
    chartItems: GenericChartItem[],
    chartSeries: string[],
    isMultipleSeries: boolean
  ): string[] {
    if (!isMultipleSeries) {
      return chartSeries
    }

    const seriesWithData = new Set<string>()

    // Check each series across all chart items to see if it has any non-zero values
    chartSeries.forEach((seriesName) => {
      const hasData = chartItems.some((item) => {
        const numJobs = item.seriesNumItems[seriesName] || 0
        return numJobs > 0
      })
      if (hasData) {
        seriesWithData.add(seriesName)
      }
    })

    const filteredChartSeries = chartSeries.filter((s) => seriesWithData.has(s))

    // Remove series properties from chart items that don't have data
    chartItems.forEach((item) => {
      chartSeries.forEach((seriesName) => {
        if (!seriesWithData.has(seriesName)) {
          delete item[seriesName]
          delete item.seriesNumItems[seriesName]
        }
      })
    })

    return filteredChartSeries
  }
}
